import { readApiError } from './apiError';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export type Transport = (path: string, init?: RequestInit) => Promise<Response>;

export interface RequestOptions extends RequestInit {
  /** Serialized as the JSON request body; use `body` for FormData or pre-encoded bodies. */
  readonly json?: unknown;
  /** Message used when the error response carries no FastAPI `detail`. */
  readonly fallbackError?: string | ((status: number) => string);
}

const DEFAULT_ERROR = 'Could not complete this request.';

/** Send a request through `transport`, throw `ApiError` on failure and parse the JSON response. */
export async function requestWith<T>(
  transport: Transport,
  path: string,
  { json, fallbackError = DEFAULT_ERROR, ...init }: RequestOptions = {},
): Promise<T> {
  const response = await transport(path, json === undefined ? init : { ...init, body: JSON.stringify(json) });
  if (!response.ok) {
    const fallback = typeof fallbackError === 'function' ? fallbackError(response.status) : fallbackError;
    throw new ApiError(await readApiError(response, fallback), response.status);
  }
  return response.status === 204 ? (undefined as T) : response.json();
}
